<template>
    <div>
        <!-- ENABLE ONLY WHEN LAB PROFILE SWITCHED TO VUE -->
        <!-- To add <breadcrumb> here when ready -->
        <div class="page-content">
            <div class="patient-name-title">
                <h1 class="mx-0">{{ patientFullName }}</h1>
                <div
                    v-if="!isLoading && patientDetails"
                    class="summarize-button-wrapper"
                >
                    <summarize-button
                        v-if="isSummarizerEnabled"
                        @click="summarizePatient"
                    />
                </div>
            </div>

            <div v-if="isLoading" class="preloader">
                <img src="assets/ajax-loader.gif" />
            </div>
            <b-card v-else footer-class="p-0" :key="cardKey">
                <patient-display
                    @load="reloadTab('tabRelationships')"
                    :mode="profileMode"
                    ref="patientDisplay"
                    @edit-saved="editSaved"
                    @show-plan="showWellnessPlan"
                    :patient="patientDetails"
                    v-on="$listeners"
                    :refreshPhotoCount="refreshPhotoCount"
                />
                <template
                    v-slot:footer
                    no-body
                    v-if="dataLoaded && !isBusinessLoading"
                >
                    <b-tabs
                        card
                        lazy
                        class="list tab-table-responsive"
                        v-if="patientDetails"
                        v-model="activeTabIndex"
                        ref="btabs"
                    >
                        <tab-history
                            v-if="
                                _hasAccess('patient.record.view') ||
                                _hasAccess('patient.communication.view')
                            "
                            :key="1 + keyComponent"
                            :open-list="historyList"
                            @open-modal="openModal"
                            @refresh-profile="reloadPageDetails"
                            @reload-document-count="reloadDocument"
                            ref="tabHistory"
                            tabname="tabHistory"
                            :patientDetails="patientDetails"
                        />
                        <tab-appointments
                            v-if="_hasAccess('schedule.access')"
                            :key="2 + keyComponent"
                            @create-appointment="createAppointment"
                            :patientDetails="patientDetails"
                            tabname="tabAppointments"
                        />
                        <tab-documents
                            ref="tabDocuments"
                            :key="3 + keyComponent"
                            :patientDetails="patientDetails"
                            :open-list="historyList"
                            tabname="tabDocuments"
                            @onSuccess="reloadAttachment"
                        />
                        <tab-notes
                            v-if="_hasAccess('patient.precaution.view')"
                            :key="4 + keyComponent"
                            :patientDetails="patientDetails"
                            tabname="tabNotes"
                        />
                        <tab-relationships
                            v-if="_hasAccess('patient.client.view')"
                            :key="5 + keyComponent"
                            :patientId="patientId"
                            :patientDetails="patientDetails"
                            tabname="tabRelationships"
                        />
                        <tab-reminders
                            v-if="_hasAccess('patient.reminder.view')"
                            :key="6 + keyComponent"
                            :patientDetails="patientDetails"
                            tabname="tabReminders"
                        />
                        <tab-wellness
                            v-if="
                                _isExtensionEnabled('WELLNESS') &&
                                _hasAccess('patient.wellness.view')
                            "
                            :key="7 + keyComponent"
                            :patientDetails="patientDetails"
                            tabname="tabWellness"
                        />
                        <tab-tasks
                            ref="tabTasks"
                            :key="8 + keyComponent"
                            :patientDetails="patientDetails"
                            tabname="tabTasks"
                        />
                    </b-tabs>
                </template>
            </b-card>

            <div v-if="patientDetails">
                <modal-new-task
                    ref="modalAddNewTask"
                    module="patient"
                    :current-client="ownerDetails"
                    :patientDetails="patientDetails"
                    @reload="reloadTab('tabTasks')"
                />

                <modal-new-reminder
                    ref="modalNewReminder"
                    :patientId="patientId"
                    @reload="onSuccessCreateReminder"
                />

                <modal-new-reminder-bundle
                    ref="modalNewReminderBundle"
                    :patientId="patientId"
                    :module="'patient'"
                    @reload="onSuccessCreateReminderBundle"
                />

                <modal-new-note
                    ref="modalNewNote"
                    @reload="reloadTab('tabNotes')"
                    :patientId="patientId"
                />

                <modal-new-relationship
                    ref="modalNewRelationship"
                    @reload="reloadTab('tabRelationships')"
                    :patientDetails="patientDetails"
                />

                <modal-new-webform
                    ref="modalNewWebform"
                    :patient="patientDetails"
                />
            </div>

            <modal-new-letter :patientDetails="patientDetails" />
            <modal-new-attachment
                @reload="reloadAttachment"
                :patientId="patientId"
            />
            <modal-enroll-in-plan
                @reload="reloadTab('tabWellness')"
                :patientId="patientId"
            />
            <modal-log-communication
                :patientDetails="patientDetails"
                ref="modalLogCommunication"
                @onsuccess="reloadHistoryTab('COMMUNICATIONS')"
            />
            <modal-send-email
                :patientDetails="patientDetails"
                ref="modalSendEmail"
                @onsuccess="reloadHistoryTab('COMMUNICATIONS')"
            />
            <modal-send-sms
                :patientDetails="patientDetails"
                ref="modalSendSms"
                @onsuccess="reloadHistoryTab('COMMUNICATIONS')"
            />
            <modal-diagnosis
                ref="modalDiagnosis"
                @onsuccess="openDiagnoses"
                :patientId="patientId"
            />
            <modal-record
                ref="modalRecord"
                @onSuccess="reloadAllRecords"
                @hidden="display.modalRecord = false"
            />
            <modal-diagnostic
                ref="modalDiagnostic"
                @reload="onSuccess"
                :patientId="patientId"
            />
            <modal-diagnostic
                ref="modalVital"
                :is-vital="true"
                @reload="onSuccessVital"
                :patientId="patientId"
            />
            <modal-problem
                ref="modalProblem"
                @reload="openHistory"
                :patientId="patientId"
            />
            <modal-medical-note
                ref="modalMedicalNote"
                :hide-patient-field="true"
            />
            <modal-bundle ref="modalBundle" @reload="onSuccessCreateBundle" />
            <modal-appointment
                ref="modalAppointment"
                @reload="reloadTab('tabAppointments')"
                module="patient"
            />
            <modal-approve-reject-request-refill
                ref="modalApproveRejectRequestRefill"
                @reload="reloadAllRecords"
                @approved="onSuccessApproveRefillRequest"
            />

            <popup-notes :key="'popupPatient' + popupKey" />

            <summarize
                v-if="summarizer.isOpen"
                :patientId="summarizer.patientId"
                @close="summarizer.isOpen = false"
            />
        </div>
    </div>
</template>

<script>
import { DateTime } from "luxon";
import patientDisplay from "@/components/patient/patient-display";
import TabHistory from "./tabs/tab-history";
import TabAppointments from "./tabs/tab-appointments";
import TabDocuments from "./tabs/tab-documents";
import TabNotes from "./tabs/tab-notes";
import TabRelationships from "./tabs/tab-relationships";
import TabReminders from "./tabs/tab-reminders";
import TabWellness from "./tabs/tab-wellness";
import TabTasks from "./tabs/tab-tasks";
import ModalNewReminder from "@/components/client-reminders/reminder-save-modal";
import modalNewReminderBundle from "@/components/reminder-bundle/modal";
import ModalNewTask from "@/components/tasks/task-update-modal";
import ModalNewLetter from "@/components/patient-documents/document-add-letter-modal";
import ModalNewAttachment from "@/components/patient-documents/document-add-attachment-modal";
import ModalEnrollInPlan from "@/components/patient-wellness-plans/wellness-modal-enroll";
import ModalNewNote from "@/components/patient-notes/note-save";
import ModalNewRelationship from "@/components/patient-relationships/relationship-save";
import ModalLogCommunication from "@/components/commons/modal-patient-log-communication";
import ModalSendEmail from "@/components/commons/modal-patient-send-email";
import ModalSendSms from "@/components/commons/modal-patient-send-sms";
import ModalDiagnosis from "@/components/commons/modal-diagnosis";
import ModalRecord from "@/module/commons/record/create-update";
import ModalDiagnostic from "@/components/patient-diagnostic/diagnostic-new-modal";
import ModalProblem from "@/components/problems/problem-save-modal";
import ModalMedicalNote from "@/module/soap/create";
import ModalBundle from "@/components/bundle/modal";
import PopupNotes from "@/components/patient/popup-notes";
import HISTORY from "@/constants/history";
import WELLNESSPLAN from "@/constants/wellness_plan";
import History from "@/services/patient-history";
import Record from "@/services/records";
import unsavePrompt from "@/mixins/unsave-prompt.js";
import ModalAppointment from "@/module/schedule/appointment/create-update/modal";
import ModalNewWebform from "@/components/webform/modal-new-webform";
import ModalApproveRejectRequestRefill from "@/components/refill-requests/approve-reject-request-refill-modal";
import PatientServices from "@/services/patient.js";
import EXTENSIONS from "@/constants/extensions";
import EVENT from "@/constants/events";
import { isEmpty } from "lodash";
import COMMUNICATIONTYPE from "@/constants/communication-types";
import SummarizeButton from "./summarize-button.vue";
import Summarize from "./summarize";

export default {
    mixins: [unsavePrompt],
    components: {
        patientDisplay,
        TabHistory,
        TabAppointments,
        TabDocuments,
        TabNotes,
        TabRelationships,
        TabReminders,
        TabWellness,
        TabTasks,
        ModalNewReminder,
        modalNewReminderBundle,
        ModalNewTask,
        ModalNewLetter,
        ModalNewAttachment,
        ModalEnrollInPlan,
        ModalNewNote,
        ModalNewRelationship,
        ModalLogCommunication,
        ModalSendEmail,
        ModalSendSms,
        ModalDiagnosis,
        ModalRecord,
        ModalDiagnostic,
        ModalProblem,
        ModalMedicalNote,
        ModalBundle,
        PopupNotes,
        ModalAppointment,
        ModalApproveRejectRequestRefill,
        ModalNewWebform,
        Summarize,
        SummarizeButton
    },
    props: {
        profileMode: {
            type: String
        },
        patientDetails: {
            type: Object
        },
        refreshPhotoCount: {
            type: Number
        }
    },
    data() {
        return {
            isLoading: false,
            record: {},
            keyComponent: 0,
            cardKey: 0,
            activeTabIndex: 0,
            historyList: null,
            dataLoaded: false,
            requestRefill: {},
            display: {
                modalRecord: false,
                modalVital: false
            },
            summarizer: {
                isOpen: false,
                patientId: null
            },
            isSummarizerEnabled: false
        };
    },
    computed: {
        patientId() {
            return this.patientDetails ? this.patientDetails.id : null;
        },
        isBusinessLoading() {
            return this.$store.getters["isBusinessLoading"];
        },

        patientFullName() {
            let result = "";

            if (this.isLoading) {
                return "Loading...";
            }

            if (this.$store.getters["isBusinessLoading"]) {
                return "Loading...";
            }

            if (this.patientDetails) {
                let { name, primary_owner, index } = this.patientDetails;
                let owner = primary_owner ? primary_owner[0] : null;

                if (owner) {
                    result = `${name} ${owner.lastname} (${index})`;
                } else {
                    result = `${name} (${index})`;
                }
            }

            return result;
        },
        ownerId() {
            let details = this.patientDetails;
            let ownerId = "";

            if (details) {
                ownerId = !_.isEmpty(details.primary_owner)
                    ? this.patientDetails.primary_owner[0].id
                    : "";
            }

            return ownerId;
        },
        ownerDetails() {
            let details = this.patientDetails;
            let owner = null;

            if (details) {
                if (!_.isEmpty(details.primary_owner)) {
                    owner = details.primary_owner[0];
                }
            }

            return owner;
        },
        popupKey() {
            return this.$store.getters["patient/popupKey"];
        },
        routeParam() {
            return this.$route.params.id;
        },
        form_dirty() {
            return this.$store.getters["patient/isDirty"];
        },
        breadcrumbs() {
            let self = this;
            let breadcrumbs = [
                {
                    text: "Patients",
                    to: {
                        name: "patient_dashboard"
                    }
                }
            ];

            if (self.$route.params.patientName) {
                breadcrumbs.push({
                    text: self.$route.params.patientName,
                    to: {
                        name: "patient_profile",
                        params: {
                            id: self.$route.params.patientId
                        }
                    },
                    active: true
                });
            } else {
                if (self.patientDetails) {
                    breadcrumbs.push({
                        text: self.patientDetails.fullname,
                        to: {
                            name: "patient_profile",
                            params: {
                                id: self.patientDetails.encoded_id
                            }
                        },
                        active: true
                    });
                }
            }

            return breadcrumbs;
        },
        vitalList() {
            return this.$store.state.reference["master-vital"];
        }
    },
    watch: {
        routeParam() {
            this.historyList = null;
            this.reloadPageDetails();
            this.cardKey++;
        },
        "$root.extensions": {
            immediate: true,
            deep: true,
            handler() {
                //IF EXTENSION LOADED & EXT-WELLNESS TRUE & HAVE PATIENT ID,
                //THEN ONLY CAN GET WELLNESS INFO
                if (this.patientId && this._isExtensionEnabled("WELLNESS")) {
                    this.getWellnessPlanInfo();
                }
            }
        },
        dataLoaded() {
            this.$nextTick(function () {
                if (Object.keys(this.$route.query).length > 0) {
                    // Check if it exists first
                    const query = this.$route.query;
                    this.reloadTab(query.tabName, query.dropdown);
                }
            });
        },
        vitalList(value) {
            /*
                open vital modal and pre-select vital type when
                modal = VITAL & modalField is available in the query
            */

            if (this.$route.query.modal && value.length > 0) {
                this.loadModalOnQuery();
            }
        },
        patientId(value) {
            // if clinic enable vetsource, call this api to trigger vetsource order
            // BE will send socket with action name patient-history-refresh
            if (
                value &&
                !_.isEmpty(this.$root.extensions) &&
                this._isExtensionEnabled(EXTENSIONS.CODE.CODE_VETSOURCE)
            ) {
                PatientServices.getPatientVetsource(value);
            }
        },
        patientDetails: {
            deep: true,
            handler(value) {
                if (this.display.modalRecord) {
                    this.$refs.modalRecord.reloadPatient({
                        patient: {
                            id: this.patientId,
                            encodedId: value.encoded_id,
                            primaryOwner: value.primary_owner,
                            weight: value.weight
                        }
                    });
                }

                if (this.$route.query.modal && this.vitalList.length > 0) {
                    this.loadModalOnQuery();
                }
            }
        },
        isBusinessLoading() {
            if (!this.isBusinessLoading && !this.patientDetails) {
                this.$router.push({ name: "patient_dashboard" });
            }
        }
    },
    created() {
        this.$tabEvent.on(EVENT.REFRESH_PATIENT_PROFILE, () => {
            this.getDetails();
        });
    },
    mounted() {
        this.reloadPageDetails();
        this.$root.$on("resend-communication", this.resendCommunication);
        this.checkSummarizerFeature();
    },
    sockets: {
        "v-broadcast"(params) {
            if (params.action === "reminder-summary-refresh") {
                if (
                    this.patientDetails &&
                    params.userId == this.$root.currentActor.id
                ) {
                    this.$store.dispatch(
                        "patient/getReminders",
                        this.patientDetails.id
                    );
                }
            }
        }
    },
    destroyed() {
        this.$store.dispatch("patient/clearDetails");
        this.$tabEvent.off(EVENT.REFRESH_PATIENT_PROFILE);
        this.$root.$off("resend-communication");
    },
    methods: {
        resendCommunication(comm) {
            console.log(comm);
            switch (comm.type?.id) {
                case COMMUNICATIONTYPE.COMMUNICATION_TYPES.SMS: {
                    // SMS
                    this.$refs.modalSendSms.open(comm);
                    break;
                }

                case COMMUNICATIONTYPE.COMMUNICATION_TYPES.EMAIL: {
                    // email
                    this.$refs.modalSendEmail.open(comm);
                    break;
                }
            }
        },
        checkSummarizerFeature() {
            setTimeout(() => {
                // $root.setting.feature_flag tidak peduli `is_enabled` dalam DB,
                // jadi semua yang ada akan tersenarai walaupun is_enabled 0 -- TODO / FIXME
                let flags = this.$root.setting?.feature_flag ?? "";
                let isEnabled = flags.split(",").includes("ai_patient_info");
                this.isSummarizerEnabled = isEnabled;
            }, 600);
        },
        summarizePatient() {
            this.summarizer.patientId = this.patientId;
            this.summarizer.isOpen = true;
        },
        async reloadAllRecords() {
            this.activeTabIndex = 0;
            this.keyComponent++;
            await this.getDetails();

            if (this.$refs.tabHistory) {
                // seem when reloading the page, the tabHistory is not yet rendered
                this.$refs.tabHistory.recordAction("ALL");
            }
            let patientId = this.patientId;
            // reload summary reminders
            this.$store.dispatch("patient/getReminders", patientId);
            this.reloadHistoryCount();
        },
        async reloadPageDetails() {
            this.activeTabIndex = 0;
            this.$store.commit("patient/setCounts", null);
            await this.getDetails();
            this.reloadHistoryCount();

            this._getSetting().then(() => {
                this.$nextTick(() => {
                    // TODO - EPIC-1000 - to add the soap encoded id in API history list and medical note list
                    this.$store.dispatch("soap/checkSoapEnableBeta");
                    this.dataLoaded = true;

                    //IF EXTENSION LOADED & EXT-WELLNESS TRUE,
                    //THEN ONLY CAN GET WELLNESS INFO
                    if (
                        !_.isEmpty(this.$root.extensions) &&
                        this._isExtensionEnabled("WELLNESS")
                    ) {
                        this.getWellnessPlanInfo();
                    }
                });
            });

            /**
             *   check request refill status when request_id query is available.
             *   if request status is PENDING = 0, open approve reject request modal.
             */

            let query = this.$route.query;

            if (query.request_id) {
                this.getRequestStatus(query.request_id);
            }
        },
        async getRequestStatus(request_id) {
            const PENDING_APPROVAL_STATUS = 0;
            const errorMessage = "Error: Unable to get refill request details";

            Record.getRecordRefillRequest(request_id)
                .then(response => {
                    if (response.message.code == 200) {
                        this.requestRefill = response.resource;

                        if (
                            this.requestRefill.status == PENDING_APPROVAL_STATUS
                        )
                            this.$refs.modalApproveRejectRequestRefill.open(
                                this.requestRefill
                            );
                    } else {
                        this._message(errorMessage, "danger");
                    }
                })
                .catch(error => {
                    this._message(errorMessage, "danger");
                    console.error(error);
                });
        },
        reloadHistoryTab(key) {
            this.reloadHistoryCount();
            this.activeTabIndex = 0;
            this.$refs.tabHistory.showList(key);
        },
        saveEditProfile() {
            this.$refs.patientDisplay.saveEdit();
        },
        editSaved(details) {
            this.$emit("edit-saved", details);
            // need to confirm after save edit patient, reload and back to history/snapshot like app or stay where u are but load the data
            // this.reloadPageDetails();

            if (!_.isEmpty(details.details)) {
                if (details.details.status !== 1) {
                    this.$store.dispatch(
                        "patient/getReminders",
                        this.patientId
                    );
                    if (
                        this.activeTabIndex == this.findTabIndex("tabReminders")
                    ) {
                        this.reloadTab("tabReminders");
                    } else if (
                        this.activeTabIndex == this.findTabIndex("tabTasks")
                    ) {
                        this.reloadTasks();
                    }
                }
            }
        },
        openModal(type, params) {
            let ownerArr = this.patientDetails.primary_owner;
            let client_id = ownerArr.length > 0 ? ownerArr[0].id : "";

            switch (type) {
                case "DIAGNOSIS":
                    this.$refs.modalDiagnosis.open();
                    break;

                case "SMS":
                    this.$refs.modalSendSms.open();
                    break;

                case "EMAIL":
                    this.$refs.modalSendEmail.open();
                    break;

                case "LOG_COMM":
                    this.$refs.modalLogCommunication.open();
                    break;

                case "VITAL":
                    this.$refs.modalVital.open(null, params);
                    this.display.modalVital = true;
                    break;

                case "MEDICAL_RECORD":
                    this.$refs.modalRecord.open({
                        action: "new",
                        clientID: client_id,
                        patient: {
                            id: this.patientId,
                            encodedId: this.patientDetails.encoded_id,
                            primaryOwner: this.patientDetails.primary_owner,
                            weight: this.patientDetails.weight
                        }
                    });
                    this.display.modalRecord = true;
                    break;

                case "DIAGNOSTIC":
                    this.$refs.modalDiagnostic.open();
                    break;

                case "PROBLEM":
                    this.$refs.modalProblem.open();
                    break;

                case "MEDICAL_NOTE":
                    this.$refs.modalMedicalNote.open({
                        patientId: this.patientId
                    });
                    break;

                case "BUNDLE":
                    this.$refs.modalBundle.open("PATIENT", this.patientDetails);
                    break;
            }
        },
        createAppointment() {
            this.$refs.modalAppointment.open({
                referrer: "patient-profile",
                id: "create-appointment",
                is_new: true,
                allDay: false,
                start: DateTime.local()
                    .startOf("hour")
                    .plus({ hour: 1 })
                    .toJSDate(),
                patient: this.patientDetails
            });
        },
        async getDetails() {
            var self = this;
            var encodedId = this.$route.params.id;
            var index = this.$route.params.index;

            this.$store.commit("patient/setPatientDetails", null);
            this.$store.commit("setIsBusinessNotFound", false);

            if (encodedId) {
                this.$store.commit("patient/clearPopupNotes"); // clear popup notes by default
                this.$store.commit("patient/setEditMode", false);
                this.$store.commit("patient/setDirty", false);

                await this.$store
                    .dispatch("patient/getPatientDetails", {
                        id: encodedId,
                        encoded: this.$route.query.encoded === "0" ? 0 : 1
                    })
                    .then(hasData => {
                        if (hasData.no_access) {
                            //no access when checking in PreventRestrictedUser middleware
                            self._message(hasData.message, "danger");
                            self.redirectToDashboard();
                        } else {
                            self.dynamicPageTitle(hasData.fullname);
                            if (hasData) {
                                setTimeout(() => {
                                    self.storeAudit();
                                    self.getPatientVital();
                                }, 1000);

                                self.$emit("set-patient-details", hasData);
                            } else {
                                self.redirectToDashboard();
                            }
                        }
                    })
                    .catch(error => {
                        console.log("FETCH ERROR:", error);
                        this.$store.commit("setIsBusinessNotFound", true);
                    });
            } else if (index) {
                this.$store.commit("patient/clearPopupNotes"); // clear popup notes by default
                this.$store.commit("patient/setEditMode", false);
                this.$store.commit("patient/setDirty", false);

                await this.$store
                    .dispatch("patient/getDirectUrlPatientDetails", {
                        encodedBusinessId: this.$route.params.encodedBusinessId,
                        index: index
                    })
                    .then(hasData => {
                        self.dynamicPageTitle(hasData.fullname);
                        if (hasData) {
                            setTimeout(() => {
                                self.storeAudit();
                                self.getPatientVital();
                            }, 1000);

                            self.$emit("set-patient-details", hasData);
                        } else {
                            this.$router.push({
                                name: "patient_dashboard"
                            });
                        }
                    })
                    .catch(error => {
                        console.log("FETCH ERROR:", error);
                        this.$store.commit("setIsBusinessNotFound", true);
                        if (!this.isBusinessLoading) {
                            this.$router.push({
                                name: "patient_dashboard"
                            });
                        }
                    });
            } else {
                this.$router.push({ name: "patient_dashboard" });
            }
        },
        async getWellnessPlanInfo() {
            let params = {
                status: WELLNESSPLAN.WELLNESS_PLAN.STATUS_ACTIVE, // 0 for ACTIVE
                page: 1
            };

            const response = await PatientServices.getWellnessPlanList(
                this.patientId,
                params
            );

            if (response && response.message && response.message.code === 200) {
                this.$store.commit("patient/setWellnessPlan", response);
            }
        },
        async storeAudit() {
            // TODO : to update store audit details
            const patientId = _.clone(this.patientId);
            const fullPath = _.clone(this.$router.currentRoute.fullPath);

            if (!patientId) {
                return;
            }

            await this._getSetting();
            await this._saveAudit({
                action: "view",
                path: "/patient/patient/view." + patientId,
                reference_id: patientId,
                fullPath
            });
        },
        async getPatientVital() {
            await this.$store.dispatch(
                "patient/getLatestVital",
                this.patientId
            );
        },
        openDiagnoses() {
            this.reloadTab(0, HISTORY.VIEWS.DIAGNOSES);
        },
        onSuccess(view) {
            // modal-problem no need to run reload history count as already has meta count in problem-list
            this.reloadHistoryCount();
            this.openHistory(view);

            if (view.toLowerCase() == "vitals") {
                this.getPatientVital();
            }
        },
        onSuccessVital(view) {
            let query = this.$route.query;

            if (query.modalField && query.modal == "VITAL") {
                delete query.modal;
                delete query.modalField;

                this.$router.replace({
                    query: query
                });
            }

            this.display.modalVital = false;
            this.onSuccess(view);
        },
        reloadHistoryCount() {
            this.$store.dispatch("patient/getHistoryCount", {
                patientId: this.patientId
            });
        },
        openHistory(view) {
            this.reloadTab(0, view);
        },
        findTabIndex(tabname) {
            return _.findIndex(this.$refs.btabs.$children, function (tab) {
                return tab.$attrs.tabname == tabname;
            });
        },
        reloadTab(tabname, historyList) {
            let tabIndex = this.findTabIndex(tabname);
            this.historyList = historyList ? historyList : null;
            this.activeTabIndex = tabIndex > 0 ? tabIndex : 0;
            this.keyComponent++;
        },
        reloadDocument() {
            setTimeout(() => {
                this.$refs.tabDocuments.getTabCounts();
            }, 500);
        },
        reloadAttachment() {
            setTimeout(() => {
                this.reloadTab("tabDocuments", "ATTACHMENTS");
                this.$refs.tabDocuments.getTabCounts(0);
            }, 500);
        },
        reloadCertificate() {
            this.reloadTab("tabDocuments", "CERTIFICATES");
            this.$refs.tabDocuments.getTabCounts();
        },
        reloadTasks() {
            this.$refs.tabTasks.loadList(0, true);
        },
        onSuccessCreateReminder() {
            this.$store.dispatch("patient/getReminders", this.patientId);
            this.reloadTab("tabReminders");
        },
        onSuccessCreateReminderBundle() {
            this.$store.dispatch("patient/getReminders", this.patientId);
            this.reloadTab("tabReminders");
        },
        onSuccessCreateBundle() {
            this.$store.dispatch("patient/getReminders", this.patientId);

            this.$store
                .dispatch("patient/getPatientDetails", {
                    id: this.$route.params.id,
                    encoded: this.$route.query.encoded === "0" ? 0 : 1
                })
                .then(details => {
                    this.$root.$emit("update-patient-details", details);
                });

            this.openHistory(HISTORY.VIEWS.RECORDS);
            this.reloadHistoryCount();
        },
        async onSuccessApproveRefillRequest(request) {
            try {
                let recordResponse = await History.getRecordDetails(
                    request.parent_record_id
                );

                if (recordResponse.message.code != 200) {
                    throw recordResponse.message.code;
                }

                let refillResponse = await Record.refillRecordDetails(
                    request.parent_record_id
                );

                this.$refs.modalRecord.open({
                    ...recordResponse.resource,
                    clientID: request.client.id,
                    action: "refill",
                    module: this.MODULE.PATIENT,
                    record_resources: refillResponse
                });

                this.display.modalRecord = true;
            } catch (error) {
                this._message("Error: Unable to initiate refill", "danger");
            }
        },
        parentFunction(code) {
            code();
        },
        showWellnessPlan() {
            this.reloadTab("tabWellness");
        },
        loadModalOnQuery() {
            if (!isEmpty(this.patientDetails)) {
                let params = null;

                if (
                    this.$route.query.modalField &&
                    this.$route.query.modal == "VITAL"
                ) {
                    let index = this.vitalList.findIndex(
                        e => e.vital == this.$route.query.modalField
                    );
                    params = { typeId: this.vitalList[index].id };

                    if (!this.display.modalVital) {
                        this.openModal(this.$route.query.modal, params);
                    }
                }
            }
        },
        redirectToDashboard() {
            this.$router.push({ name: "patient_dashboard" });
        }
    }
};
</script>

<style lang="scss" scoped>
.preloader {
    margin-left: 15px;
}
.patient-name-title {
    display: flex;
    padding: 17px 10px;
    gap: 10px;

    h1 {
        margin: 0;
    }

    .summarize-button-wrapper {
        align-content: center;
    }
}
</style>
